import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { TreePine, Users, Share2, FileDown, GitBranch, Plus, Palette, ArrowRight, Heart, User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FamilyTreeLandingPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
      // If logged in, fetch trees
      const treesRes = await axios.get(`${API}/family-trees`, { withCredentials: true });
      setTrees(treesRes.data || []);
    } catch (error) {
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/tree-builder');
    } else {
      sessionStorage.setItem('authRedirectPath', '/tree-builder');
      const redirectUrl = window.location.origin + '/home';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </Link>
          
          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button 
              variant="ghost"
              onClick={() => navigate('/memorial-tribute')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Memorial Tribute
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/family-tree')}
              className="text-primary bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <TreePine className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Family Tree
            </Button>
          </div>

          {/* Right Side */}
          {currentUser ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                {currentUser.picture ? (
                  <img src={currentUser.picture} alt={currentUser.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                )}
                <span className="text-sm font-sans text-foreground hidden sm:block">{currentUser.name}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/home'); }}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Button 
              onClick={handleGetStarted}
              data-testid="nav-start-tree-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
            >
              Start Building
            </Button>
          )}
        </div>
      </nav>

      {/* Hero Section - Different for logged in users */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <TreePine className="w-16 h-16 text-primary mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight text-foreground mb-6">
              {currentUser ? 'Your Family Trees' : 'Build Your Family Tree'}
            </h1>
            <p className="text-xl font-sans text-foreground-muted leading-relaxed mb-8 max-w-2xl mx-auto">
              {currentUser 
                ? 'Create a new family tree or continue working on your existing ones.'
                : 'Create a beautiful, interactive family tree. Add generations, connect relatives, and preserve your family\'s history for future generations.'}
            </p>
            
            {currentUser ? (
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  onClick={() => navigate('/tree-builder')}
                  data-testid="hero-new-tree-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-10 py-4 text-lg font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
                >
                  <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Create New Family Tree
                </Button>
                {trees.length > 0 && (
                  <Button 
                    onClick={() => {
                      const element = document.getElementById('your-trees');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    variant="outline"
                    data-testid="hero-view-trees-btn"
                    className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full px-10 py-4 text-lg font-serif tracking-wide transition-all"
                  >
                    Go to Your Family Trees
                    <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            ) : (
              <Button 
                onClick={handleGetStarted}
                data-testid="hero-start-tree-btn"
                className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-10 py-4 text-lg font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
              >
                Start Your Family Tree
                <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
              </Button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Your Family Trees Section (Logged in users only) */}
      {currentUser && trees.length > 0 && (
        <section id="your-trees" className="py-16 px-6 bg-paper">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-serif text-foreground mb-8">Your Family Trees</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trees.map(tree => (
                <motion.div
                  key={tree.tree_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  onClick={() => navigate(`/tree-builder/${tree.tree_id}`)}
                  className="bg-white border border-border shadow-card rounded-xl p-6 cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <TreePine className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-serif text-foreground">{tree.name}</h3>
                      <p className="text-sm text-muted-foreground">{tree.node_count || 0} members</p>
                    </div>
                  </div>
                  <span className="text-primary font-sans text-sm flex items-center">
                    Continue editing <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Visual Tree Demo - Only for non-logged in users or those without trees */}
      {(!currentUser || trees.length === 0) && (
        <section className="py-16 px-6 bg-paper">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white border border-border rounded-2xl p-8 shadow-card"
            >
              {/* Simple Tree Visualization */}
              <div className="flex flex-col items-center">
                {/* Grandparents Level */}
                <div className="flex justify-center space-x-32 mb-4">
                  <div className="flex space-x-4">
                    <div className="w-16 h-16 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center">
                      <span className="text-xs font-sans text-amber-700">Dada</span>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center">
                      <span className="text-xs font-sans text-amber-700">Dadi</span>
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <div className="w-16 h-16 rounded-full bg-rose-100 border-2 border-rose-300 flex items-center justify-center">
                      <span className="text-xs font-sans text-rose-700">Nana</span>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-rose-100 border-2 border-rose-300 flex items-center justify-center">
                      <span className="text-xs font-sans text-rose-700">Nani</span>
                    </div>
                  </div>
                </div>
                
                {/* Connecting Lines */}
                <div className="flex justify-center space-x-32 mb-4">
                  <div className="w-20 h-8 border-l-2 border-r-2 border-b-2 border-border rounded-b-lg"></div>
                  <div className="w-20 h-8 border-l-2 border-r-2 border-b-2 border-border rounded-b-lg"></div>
                </div>
                
                {/* Parents Level */}
                <div className="flex justify-center space-x-8 mb-4">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 border-2 border-emerald-400 flex items-center justify-center">
                    <span className="text-xs font-sans text-emerald-700">Papa</span>
                  </div>
                  <div className="w-6 h-0.5 bg-primary self-center"></div>
                  <div className="w-20 h-20 rounded-full bg-emerald-100 border-2 border-emerald-400 flex items-center justify-center">
                    <span className="text-xs font-sans text-emerald-700">Mummy</span>
                  </div>
                </div>
                
                {/* Line to children */}
                <div className="w-0.5 h-8 bg-border mb-4"></div>
                
                {/* Children Level */}
                <div className="flex justify-center space-x-6">
                  <div className="w-16 h-16 rounded-full bg-sky-100 border-2 border-sky-400 flex items-center justify-center">
                    <span className="text-xs font-sans text-sky-700">You</span>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-sky-100 border-2 border-sky-400 flex items-center justify-center">
                    <span className="text-xs font-sans text-sky-700">Sibling</span>
                  </div>
                </div>
              </div>
              
              <p className="text-center text-sm font-sans text-muted-foreground mt-8">
                Each family unit gets a unique color for easy identification
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-serif tracking-tight text-center text-foreground mb-16">
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                icon: Plus,
                title: "Add First Person",
                description: "Click anywhere on the canvas to add yourself or any family member to start."
              },
              {
                step: "2",
                icon: GitBranch,
                title: "Grow Branches",
                description: "Add parents above, children below, and siblings or spouse to the sides."
              },
              {
                step: "3",
                icon: Users,
                title: "Add Family Units",
                description: "Create forks for multiple children or connect spouse's family tree."
              },
              {
                step: "4",
                icon: Share2,
                title: "Share & Export",
                description: "Invite family members to contribute or export as PDF to share."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-serif mx-auto mb-4">
                  {item.step}
                </div>
                <item.icon className="w-8 h-8 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-xl font-serif text-foreground mb-2">{item.title}</h3>
                <p className="text-sm font-sans text-foreground-muted leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-paper">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-serif tracking-tight text-center text-foreground mb-16">
            Powerful Features
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: GitBranch,
                title: "Unlimited Branches",
                description: "Add as many generations and family members as you want. No limits."
              },
              {
                icon: Palette,
                title: "Color-Coded Families",
                description: "Each family unit (parents + children) gets a unique color for easy viewing."
              },
              {
                icon: Users,
                title: "Invite Contributors",
                description: "Send email invites to family members to help build the tree together."
              },
              {
                icon: Share2,
                title: "Shareable Link",
                description: "Generate a view-only link to share your tree with anyone."
              },
              {
                icon: FileDown,
                title: "PDF Export",
                description: "Download your family tree as a beautiful PDF to print or share."
              },
              {
                icon: TreePine,
                title: "Multiple Trees",
                description: "Create separate trees for different family lines - Gupta Khandan, Sharma Parivar, etc."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-border shadow-card rounded-xl p-6 hover:shadow-md transition-all"
              >
                <feature.icon className="w-10 h-10 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-serif text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm font-sans text-foreground-muted leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <TreePine className="w-12 h-12 mx-auto mb-6 opacity-90" strokeWidth={1.5} />
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight mb-6">
            {currentUser ? 'Ready to Build?' : 'Start Building Your Family Tree'}
          </h2>
          <p className="text-lg font-sans mb-8 opacity-90">
            Preserve your family's history. Connect generations. Create a legacy.
          </p>
          <Button 
            onClick={handleGetStarted}
            data-testid="cta-start-tree-btn"
            className="bg-white text-primary hover:bg-gray-100 rounded-full px-10 py-4 text-lg font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
          >
            {currentUser ? 'Create New Family Tree' : 'Get Started - It\'s Free'}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-background border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <TreePine className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="text-xl font-serif font-semibold text-foreground">Sarmoh</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/terms" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
          <div className="text-center pt-6 border-t border-border">
            <p className="text-sm font-sans text-muted-foreground">
              © 2026 TLML Mediatech Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
